import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import moment from 'moment';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { Timesheet } from 'src/app/models/Timesheet';

import { TimesheetAiValidatorService } from '../timesheet-ai-validator.service';
import { TimesheetsService } from '../timesheets.service';
import { TimeslotListService } from '../timeslot-list.service';

@Component({
    selector: 'app-public-timeslot-list',
    templateUrl: './public-timeslot-list.component.html',
    styleUrls: ['./public-timeslot-list.component.scss'],
    providers: [TimeslotListService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class PublicTimeslotListComponent implements OnInit, OnDestroy {
    private subscriptions = new Subscription();

    @Input() timesheet: Timesheet;
    @Input() isEditable: boolean = true;

    @Output() changeEvent: EventEmitter<void> = new EventEmitter<void>();

    constructor(
        private timeslotListService: TimeslotListService,
        // Next imports are required in timeslotService, please don't remove them
        private store: Store,
        private modalService: NgbModal,
        private logger: NGXLogger,
        private toastr: ToastrService,
        private timesheetsService: TimesheetsService, // Last import
        private tsAiValidator: TimesheetAiValidatorService,
    ) {}

    ngOnInit(): void {}

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    openAddTimeslotModal = this.timeslotListService.openAddTimeslotModal;
    openEditTimeslotModal = this.timeslotListService.openEditTimeslotModal;
    removeTimeslot = this.timeslotListService.removeTimeslot;
    getBase64 = this.timeslotListService.getBase64;
    getFileName = this.timeslotListService.getFileName;

    // ===== AI validation per timeslot =====
    isValidatingAttachment: Record<string, boolean> = {};
    validationProgress: Record<string, number> = {};
    validationTimers: Record<string, any> = {};

    validateTimeslot(timeslot: any): void {
        try {
            const pdfUrl = timeslot?.pdfUrl || '';
            if (!pdfUrl) {
                this.toastr.info('Geen PDF om te valideren voor deze rij.');
                return;
            }
            const employeeName = this.timesheet?.employee ? `${this.timesheet.employee.firstName} ${this.timesheet.employee.lastName}` : '';
            const workedHoursRaw = (Number(timeslot?.minutes || 0) + Number(timeslot?.leaveMinutes || 0)) / 60;
            const daysWorked = workedHoursRaw / 8;
            const invoiceByDay = !!timeslot?.project?.invoiceByDay;
            const hoursPerDay = Number(timeslot?.project?.hoursPerDay ?? 8);
            const expectedHours = invoiceByDay ? Number((daysWorked * hoursPerDay).toFixed(2)) : Number(workedHoursRaw.toFixed(2));
            const uploadedMonthISO = this.timesheet?.expirationDate
                ? moment(this.timesheet.expirationDate).startOf('month').format('YYYY-MM-01')
                : moment().startOf('month').format('YYYY-MM-01');
            const timesheetTypeHint = (timeslot?.project?.timesheetType || '').toString().trim();

            this.isValidatingAttachment[timeslot.uuid] = true;
            this.startProgress(timeslot.uuid);
            this.tsAiValidator
                .validate(pdfUrl, {
                    employeeName,
                    expectedHours,
                    monthLabel: undefined,
                    uploadedMonthISO,
                    timesheetTypeHint,
                })
                .then((result) => {
                    // Persist compact validation to backend
                    const payload = {
                        consultantName: employeeName || undefined,
                        timesheetType: (result.type || timesheetTypeHint || '').trim() || undefined,
                        periodISO: (result.details?.pdfMonthISO as any) || uploadedMonthISO || undefined,
                        signed: result.details?.signed,
                        hours: typeof result.details?.parsedHours === 'number' ? result.details.parsedHours : expectedHours,
                    };
                    this.timesheetsService.saveTimeslotValidation(timeslot.uuid, payload).subscribe({
                        next: (updated) => {
                            const refreshed = updated?.timeslots?.find((t) => t.uuid === timeslot.uuid);
                            if (refreshed) {
                                timeslot.validatedConsultantName = refreshed.validatedConsultantName;
                                timeslot.validatedTimesheetType = refreshed.validatedTimesheetType;
                                timeslot.validatedPeriodISO = refreshed.validatedPeriodISO;
                                timeslot.validatedSigned = refreshed.validatedSigned;
                                timeslot.validatedHours = refreshed.validatedHours;
                            }
                            this.toastr.success('Validatie opgeslagen.');
                            this.changeEvent.emit();
                        },
                        error: () => {
                            this.toastr.error('Opslaan van validatie mislukt.');
                        },
                    });
                })
                .catch(() => {
                    this.toastr.error('AI-validatie kon niet worden uitgevoerd.');
                })
                .finally(() => {
                    this.isValidatingAttachment[timeslot.uuid] = false;
                    this.stopProgress(timeslot.uuid);
                });
        } catch {
            this.toastr.error('Kon timesheet niet valideren.');
        }
    }

    private startProgress(id: string): void {
        this.validationProgress[id] = 10;
        this.validationTimers[id] = setInterval(() => {
            const cur = this.validationProgress[id] || 10;
            this.validationProgress[id] = Math.min(95, cur + 7);
        }, 300);
    }

    private stopProgress(id: string): void {
        const t = this.validationTimers[id];
        if (t) clearInterval(t);
        this.validationProgress[id] = 100;
        setTimeout(() => {
            delete this.validationProgress[id];
        }, 800);
        delete this.validationTimers[id];
    }
}
